<template>
  <div class="pt-4 px-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>Validations</h2>
    </div>

    <div class="card">
      <div class="card-header">
        Why assets aren't supported on an ad group
      </div>
      <div class="card-body">
        <form @submit.prevent="onSubmit">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Customer</label>
              <select class="form-select" v-model.number="form.customerId" required>
                <option value="" disabled>Select a customer...</option>
                <option v-for="customer in customers" :key="customer.customerId" :value="customer.customerId">
                  {{ customer.customerName }} ({{ customer.customerId }})
                </option>
              </select>
            </div>

            <div class="col-md-4">
              <label class="form-label">Ad Group ID</label>
              <input type="number" class="form-control" v-model.number="form.adGroupId" placeholder="e.g. 12345" required />
            </div>

            <div class="col-md-4">
              <label class="form-label">Asset Owner</label>
              <input type="text" class="form-control" v-model.trim="form.assetOwner" placeholder="e.g. Outfront" required />
            </div>

            <div class="col-md-6">
              <label class="form-label">Face Positions</label>
              <select class="form-select" multiple v-model="form.mediaPositions">
                <option value="LEFT">LEFT</option>
                <option value="RIGHT">RIGHT</option>
                <option value="BACK">BACK</option>
                <option value="CENTER">CENTER</option>
              </select>
              <div class="form-text">Hold Cmd/Ctrl (or Shift) to select multiple.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label">Media Product</label>
              <input type="text" class="form-control" v-model.trim="form.mediaProduct" placeholder="e.g. Static Poster" required />
            </div>

            <div class="col-md-12">
              <label class="form-label">Asset IDs (comma-separated)</label>
              <input type="text" class="form-control" v-model.trim="assetIdsInputString" placeholder="e.g. 101, 202, 303" />
              <div class="form-text">Enter one or more IDs separated by commas. Spaces are ok.</div>
            </div>

          </div>

          <div class="mt-4 d-flex align-items-center gap-2">
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              <span v-if="!submitting">Run Validation</span>
              <span v-else>
                <span class="spinner-border spinner-border-sm me-2"></span>
                Running...
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="results && results.length" class="card mt-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          Validation Results
          <span v-if="!hasAnyEligible" class="badge bg-warning text-dark ms-2">Backend returns only ineligible assets</span>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-secondary" @click="copyFilteredIds">Copy Asset IDs</button>
          <button class="btn btn-sm btn-outline-secondary" @click="exportCsv">Export CSV</button>
          <button class="btn btn-sm btn-outline-secondary" @click="showRaw = !showRaw">
            {{ showRaw ? 'Hide Raw' : 'Show Raw' }}
          </button>
        </div>
      </div>
      <div class="card-body">
        <div class="mb-3 p-3 bg-light rounded border">
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <span class="text-muted small me-2">Applied Criteria:</span>
            <span class="badge bg-secondary">Customer: {{ selectedCustomerName }} ({{ lastPayload.customerId || '-' }})</span>
            <span class="badge bg-secondary">Ad Group: {{ lastPayload.adGroupId || '-' }}</span>
            <span class="badge bg-secondary">Owner: {{ lastPayload.assetOwner || '-' }}</span>
            <span class="badge bg-secondary" v-if="(lastPayload.mediaPositions || []).length">Positions: {{ (lastPayload.mediaPositions || []).join(', ') }}</span>
            <span class="badge bg-secondary">Product: {{ lastPayload.mediaProduct || '-' }}</span>
            <span class="badge bg-secondary" v-if="(lastPayload.assetIds || []).length">Assets: {{ (lastPayload.assetIds || []).length }}</span>
            <button class="btn btn-sm btn-outline-dark ms-auto" @click="clearFilters" v-if="hasAnyFilters">Clear Filters</button>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <div class="col-md-4" :class="hasAnyEligible ? 'col-md-3' : 'col-md-4'">
            <div class="p-3 border rounded d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">Total</div>
                <div class="h5 mb-0">{{ results.length }}</div>
              </div>
              <span class="badge bg-secondary">All</span>
            </div>
          </div>
          <div v-if="hasAnyEligible" class="col-md-3">
            <div class="p-3 border rounded d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">Eligible</div>
                <div class="h5 mb-0">{{ eligibleCount }}</div>
              </div>
              <span class="badge bg-success">Eligible</span>
            </div>
          </div>
          <div class="col-md-4" :class="hasAnyEligible ? 'col-md-3' : 'col-md-4'">
            <div class="p-3 border rounded d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">Ineligible</div>
                <div class="h5 mb-0">{{ ineligibleCount }}</div>
              </div>
              <span class="badge bg-danger">Ineligible</span>
            </div>
          </div>
          <div class="col-md-4" :class="hasAnyEligible ? 'col-md-3' : 'col-md-4'">
            <div class="p-3 border rounded d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted small">Unique Failure Reasons</div>
                <div class="h5 mb-0">{{ availableReasons.length }}</div>
              </div>
              <span class="badge bg-warning text-dark">Reasons</span>
            </div>
          </div>
        </div>

        <div class="row g-3 align-items-end mb-3">
          <div class="col-md-3">
            <label class="form-label">Eligibility</label>
            <select class="form-select" v-model="eligibilityFilter">
              <option v-if="hasAnyEligible" value="all">All</option>
              <option v-if="hasAnyEligible" value="eligible">Eligible</option>
              <option value="ineligible">Ineligible</option>
            </select>
            <div v-if="!hasAnyEligible" class="form-text">Backend returns only ineligible assets</div>
          </div>
          <div class="col-md-5">
            <label class="form-label">Failure Reasons</label>
            <select class="form-select" multiple v-model="reasonFilters">
              <option v-for="reason in availableReasons" :key="reason" :value="reason">
                {{ humanizeReason(reason) }} ({{ reasonCounts[reason] || 0 }})
              </option>
            </select>
            <div class="form-text">Hold Cmd/Ctrl (or Shift) to select multiple.</div>
          </div>
          <div class="col-md-2">
            <label class="form-label">Sort</label>
            <select class="form-select" v-model="sortKey">
              <option value="assetId">Asset ID</option>
              <option value="owner">Owner</option>
              <option value="classRef">Class</option>
            </select>
          </div>
          <div class="col-md-1">
            <label class="form-label">Dir</label>
            <select class="form-select" v-model="sortDir">
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
          <div class="col-md-1">
            <label class="form-label">Group</label>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="groupByReason" v-model="groupByReason" />
              <label class="form-check-label" for="groupByReason">By Reason</label>
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Search Asset ID</label>
            <input class="form-control" v-model.trim="assetSearch" placeholder="e.g. 36807693" />
          </div>
        </div>

        <div v-if="reasonFilters.length" class="mb-2">
          <span v-for="r in reasonFilters" :key="r" class="badge bg-dark me-1" @click="toggleReasonFilter(r)" style="cursor: pointer;">
            {{ humanizeReason(r) }} ✕
          </span>
        </div>

        <div v-if="groupByReason">
          <div v-for="grp in groupedResults" :key="grp.reason" class="mb-4">
            <div class="d-flex align-items-center mb-2">
              <h6 class="mb-0">{{ humanizeReason(grp.reason) }}</h6>
              <span class="badge bg-secondary ms-2">{{ grp.rows.length }}</span>
            </div>
            <div class="table-responsive">
              <table class="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Provider</th>
                    <th>Owner</th>
                    <th>Class</th>
                    <th>Found</th>
                    <th>Eligible</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in grp.rows" :key="row.assetId">
                    <td class="fw-semibold">{{ row.assetId }}</td>
                    <td>{{ row.providerId ?? '-' }}</td>
                    <td class="text-nowrap">{{ row.assetOwner || '-' }}</td>
                    <td class="text-nowrap">{{ row.classRef || '-' }}</td>
                    <td><span class="badge" :class="row.found ? 'bg-success' : 'bg-secondary'">{{ row.found ? 'Yes' : 'No' }}</span></td>
                    <td><span class="badge" :class="row.eligible ? 'bg-success' : 'bg-danger'">{{ row.eligible ? 'Eligible' : 'Ineligible' }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="bg-gray-500 text-uppercase">
              <tr>
                <th>Asset ID</th>
                <th>Provider</th>
                <th>Owner</th>
                <th>Class</th>
                <th>Found</th>
                <th>Provider OK</th>
                <th>Owner OK</th>
                <th>ClassRef OK</th>
                <th>Eligible</th>
                <th>Failure Reasons</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredAndSortedResults" :key="row.assetId" :class="row.eligible ? 'table-success' : 'table-danger'">
                <td class="fw-semibold">{{ row.assetId }}</td>
                <td>
                  <span v-if="row.providerId !== null && row.providerId !== undefined">{{ row.providerId }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-nowrap">{{ row.assetOwner || '-' }}</td>
                <td class="text-nowrap">{{ row.classRef || '-' }}</td>
                <td>
                  <span class="badge" :class="row.found ? 'bg-success' : 'bg-secondary'">{{ row.found ? 'Yes' : 'No' }}</span>
                </td>
                <td>
                  <span class="badge" :class="row.provider_ok ? 'bg-success' : 'bg-danger'">{{ row.provider_ok ? 'Yes' : 'No' }}</span>
                </td>
                <td>
                  <span class="badge" :class="row.owner_ok ? 'bg-success' : 'bg-danger'">{{ row.owner_ok ? 'Yes' : 'No' }}</span>
                </td>
                <td>
                  <span class="badge" :class="row.classref_ok ? 'bg-success' : 'bg-danger'">{{ row.classref_ok ? 'Yes' : 'No' }}</span>
                </td>
                <td>
                  <span class="badge" :class="row.eligible ? 'bg-success' : 'bg-danger'">{{ row.eligible ? 'Eligible' : 'Ineligible' }}</span>
                </td>
                <td>
                  <span v-if="row.failure_reasons && row.failure_reasons.length">
                    <span v-for="r in row.failure_reasons" :key="r" class="badge me-1" :class="r === 'assetOwner_mismatch' ? 'bg-warning text-dark' : 'bg-secondary'" @click="toggleReasonFilter(r)" style="cursor: pointer;">
                      {{ humanizeReason(r) }}
                    </span>
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
              <tr v-if="filteredAndSortedResults.length === 0">
                <td colspan="10" class="text-center text-muted py-4">No results match the current filters.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="showRaw" class="mt-3">
          <div class="bg-light border rounded p-3">
            <pre class="mb-0 small">{{ pretty(rawResponse) }}</pre>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import CustomerService from "@/services/CustomerService";
import ValidationService from "@/services/ValidationService";

export default {
  name: "Validations",
  data() {
    return {
      customers: [],
      form: {
        customerId: "",
        adGroupId: null,
        assetOwner: "",
        mediaPositions: [],
        mediaProduct: "",
      },
      assetIdsInputString: "",
      submitting: false,
      results: [],
      rawResponse: null,
      showRaw: false,
      eligibilityFilter: 'all',
      reasonFilters: [],
      assetSearch: '',
      sortKey: 'assetId',
      sortDir: 'asc',
      groupByReason: false,
      lastPayload: {},
      response: null,
    };
  },
  computed: {
    selectedCustomerName() {
      const match = this.customers.find(c => c.customerId === this.lastPayload.customerId);
      return match ? match.customerName : '-';
    },
    hasAnyEligible() {
      return this.results.some(r => r.eligible === true);
    },
    hasAnyIneligible() {
      return this.results.some(r => r.eligible === false);
    },
    eligibleCount() {
      return this.results.filter(r => r.eligible === true).length;
    },
    ineligibleCount() {
      return this.results.filter(r => r.eligible === false).length;
    },
    availableReasons() {
      const set = new Set();
      this.results.forEach(r => (r.failure_reasons || []).forEach(fr => set.add(fr)));
      return Array.from(set).sort();
    },
    reasonCounts() {
      const counts = {};
      this.results.forEach(r => {
        (r.failure_reasons || []).forEach(fr => {
          counts[fr] = (counts[fr] || 0) + 1;
        });
      });
      return counts;
    },
    filteredResults() {
      let rows = [...this.results];
      if (this.eligibilityFilter === 'eligible') {
        rows = rows.filter(r => r.eligible === true);
      } else if (this.eligibilityFilter === 'ineligible') {
        rows = rows.filter(r => r.eligible === false);
      }
      if (this.reasonFilters.length > 0) {
        rows = rows.filter(r => {
          const reasons = r.failure_reasons || [];
          return this.reasonFilters.every(sel => reasons.includes(sel));
        });
      }
      if (this.assetSearch) {
        const q = this.assetSearch.trim();
        rows = rows.filter(r => String(r.assetId).includes(q));
      }
      return rows;
    },
    filteredAndSortedResults() {
      const rows = [...this.filteredResults];
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return rows.sort((a, b) => {
        const key = this.sortKey;
        let av, bv;
        if (key === 'assetId') {
          av = Number(a.assetId); bv = Number(b.assetId);
        } else if (key === 'owner') {
          av = (a.assetOwner || '').toLowerCase();
          bv = (b.assetOwner || '').toLowerCase();
        } else if (key === 'classRef') {
          av = (a.classRef || '').toLowerCase();
          bv = (b.classRef || '').toLowerCase();
        } else {
          av = a[key]; bv = b[key];
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
    groupedResults() {
      const map = new Map();
      for (const row of this.filteredAndSortedResults) {
        const reasons = row.failure_reasons && row.failure_reasons.length ? row.failure_reasons : ['Unspecified'];
        for (const r of reasons) {
          if (!map.has(r)) map.set(r, []);
          map.get(r).push(row);
        }
      }
      return Array.from(map.entries()).map(([reason, rows]) => ({ reason, rows }));
    },
    hasAnyFilters() {
      return this.eligibilityFilter !== 'all' || this.reasonFilters.length > 0 || this.assetSearch;
    }
  },
  mounted() {
    this.loadCustomers();
  },
  methods: {
    loadCustomers() {
      const service = new CustomerService();
      service.list().then((res) => {
        this.customers = res.data;
      }).catch((err) => console.error("Failed to load customers", err));
    },
    pretty(obj) {
      try { return JSON.stringify(obj, null, 2); } catch { return obj; }
    },
    parseAssetIds(input) {
      if (!input) return [];
      return input
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(s => Number(s))
        .filter(n => Number.isFinite(n));
    },
    humanizeReason(reason) {
      const map = {
        assetOwner_mismatch: 'Asset owner mismatch',
        classRef_not_eligible: 'Class not eligible',
        providerId_null: 'Provider missing',
      };
      return map[reason] || reason.replace(/_/g, ' ');
    },
    toggleReasonFilter(reason) {
      if (this.reasonFilters.includes(reason)) {
        this.reasonFilters = this.reasonFilters.filter(r => r !== reason);
      } else {
        this.reasonFilters = [...this.reasonFilters, reason];
      }
    },
    clearFilters() {
      this.eligibilityFilter = this.hasAnyEligible ? 'all' : 'ineligible';
      this.reasonFilters = [];
      this.assetSearch = '';
      this.groupByReason = false;
      this.sortKey = 'assetId';
      this.sortDir = 'asc';
    },
    copyFilteredIds() {
      const ids = this.filteredResults.map(r => r.assetId).join(',');
      if (!ids) return;
      navigator.clipboard?.writeText(ids).catch(() => {});
    },
    exportCsv() {
      const rows = this.filteredResults;
      if (!rows.length) return;
      const headers = ['assetId','providerId','assetOwner','classRef','found','provider_ok','owner_ok','classref_ok','eligible','failure_reasons'];
      const csv = [headers.join(',')]
        .concat(rows.map(r => [
          r.assetId,
          r.providerId ?? '',
          `"${(r.assetOwner || '').replace(/"/g, '""')}"`,
          `"${(r.classRef || '').replace(/"/g, '""')}"`,
          r.found,
          r.provider_ok,
          r.owner_ok,
          r.classref_ok,
          r.eligible,
          `"${(r.failure_reasons || []).join('|').replace(/"/g, '""')}"`,
        ].join(',')))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'validation_results.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    onSubmit() {
      this.submitting = true;
      this.results = [];
      this.rawResponse = null;

      const payload = {
        customerId: this.form.customerId ? Number(this.form.customerId) : null,
        adGroupId: this.form.adGroupId ? Number(this.form.adGroupId) : null,
        assetOwner: this.form.assetOwner || null,
        mediaPositions: this.form.mediaPositions,
        mediaProduct: this.form.mediaProduct || null,
        assetIds: this.parseAssetIds(this.assetIdsInputString),
      };
      this.lastPayload = payload;

      const service = new ValidationService();
      service
        .validateAssetsNotSupportedOnAdGroup(payload)
        .then((res) => {
          this.rawResponse = res;
          const results = Array.isArray(res) ? res : (res.results || res.data?.results || []);
          this.results = results.map(r => ({ ...r, eligible: r.eligible === true ? true : false }));
          this.submitting = false;
          this.eligibilityFilter = this.hasAnyEligible ? 'all' : 'ineligible';
          this.reasonFilters = [];
          this.assetSearch = '';
          this.groupByReason = false;
          this.sortKey = 'assetId';
          this.sortDir = 'asc';
        })
        .catch((err) => {
          this.rawResponse = err;
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.card-header { font-weight: 600; }
pre { white-space: pre-wrap; word-break: break-word; }
.table thead th { position: sticky; top: 0; z-index: 1; }
.badge { font-size: 0.75rem; }
</style>
